import { historyWords as words } from "@/components/history/history-copy";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { peopleApi } from "@/lib/api/people";
import type { ImportKind, ImportRowInput, LearnerChoice } from "@/lib/api/imports";

export function LearnerChoices({ rows, choices, labels, kind, onChange }: {
  rows: ImportRowInput[]; choices: Record<string, LearnerChoice>; labels: Record<string, string>;
  kind: ImportKind; onChange: (key: string, value: LearnerChoice | null, label?: string) => void;
}) {
  const [search, setSearch] = useState("");
  const people = useQuery({ queryKey: ["import-learner-choices", search],
    queryFn: () => peopleApi.list({ view: "all", relationship: ["student"], q: search || undefined, page_size: 100 }) });
  const learners = rows.filter((r) => r.entity_kind === "student" && !r.data?.student_id);
  if (!learners.length) return <p>{words.eachLearnerHasARecordedSourceIDTheDryRunVerifiesTheRecord}</p>;
  return <div className="grid gap-4 text-ui">
    <p>{words.chooseEachLearnerSRecordExplicitlyANameOrDeliveryAddressNeverChooses}</p>
    <label className="grid gap-1">{words.findAStudentRecord}<input className="field" value={search} onChange={(e) => setSearch(e.target.value)} /></label>
    {people.isPending && <p role="status">{words.readingStudentRecords}</p>}
    {people.isError && <p role="alert">{words.studentRecordsCouldNotBeRead}<button type="button" onClick={() => void people.refetch()}>{words.tryAgain}</button></p>}
    {learners.map((r) => {
      const name = String(r.data?.legal_name ?? `${r.data?.given_name ?? ""} ${r.data?.family_name ?? ""}`).trim();
      const choice = choices[r.import_key];
      const chosenId = choice?.action === "link" ? choice.student_id : null;
      return <fieldset key={r.import_key} className="grid gap-2 border-t border-rule pt-3">
        <legend className="font-medium">{name || words.unnamedSourceLearner}</legend>
        <p className="break-all text-muted-foreground">{String(r.data?.delivery_email ?? r.data?.email ?? words.noContactRecorded)}</p>
        <label className="grid gap-1">{words.decisionFor}{name}<select value={choice?.action ?? ""} onChange={(e) => {
          const action = e.target.value;
          // Link stays undecided until a person is explicitly selected.
          onChange(r.import_key, action === "create" || action === "leave_out" ? { action } : null);
        }}>
          <option value="">{words.chooseAStudentRecordBelowCreateOrLeaveOut}</option>
          {chosenId && <option value="link">{words.linkTo}{labels[r.import_key] ?? "the student record you chose"}</option>}
          <option value="create">{kind === "historical" ? words.createAHistoricalRecord : words.createAStudentRecord}</option>
          <option value="leave_out">{words.leaveOut}</option>
        </select></label>
        <label className="grid gap-1">{words.link}{name} {words.toThePersonIChoose}<select value={chosenId ?? ""} onChange={(e) => {
          const person = people.data?.rows.find((p) => p.student_id === e.target.value);
          if (person?.student_id) onChange(r.import_key, { action: "link", student_id: person.student_id }, `${person.display_name ?? words.unnamedStudent} · ${person.sid ?? words.noSID}`);
          else onChange(r.import_key, null);
        }}>
          <option value="">{words.chooseAnExistingStudentRecord}</option>
          {chosenId && !people.data?.rows.some((p) => p.student_id === chosenId) && <option value={chosenId}>{labels[r.import_key] ?? words.theStudentRecordYouChose}</option>}
          {people.data?.rows.filter((p) => p.student_id).map((p) => <option key={p.person_id} value={p.student_id!}>{p.display_name ?? words.unnamedStudent} · {p.sid ?? words.noSID}</option>)}
        </select></label>
      </fieldset>;
    })}
  </div>;
}
